/**
 * Configuration for DoorDash API
 *
 * This file handles environment-specific configuration
 * using the existing server environment detection.
 */

import { getURL, ServerBaseUrlKey, ServerEnvironment } from '~/servers/servers';

/**
 * Default environment fallback
 */
const DEFAULT_ENVIRONMENT = 'uat' as const;

/**
 * Get the current environment using the existing server detection
 * Maps ServerEnvironment enum values to the DoorDash API environment strings
 */
export const getEnvironment = (): string => {
    const serverEnv = getURL(ServerBaseUrlKey.ENVIRONMENT);

    if (!serverEnv) {
        return DEFAULT_ENVIRONMENT;
    }

    const envMap: Partial<Record<ServerEnvironment, string>> = {
        [ServerEnvironment.PRODUCTION]: 'prod',
        [ServerEnvironment.UAT]: 'uat',
        [ServerEnvironment.CN_UAT]: 'uat',
        [ServerEnvironment.DHL_UAT]: 'uat',
        [ServerEnvironment.DEMO]: 'demo',
        [ServerEnvironment.STAGE]: 'stage'
    };

    return envMap[serverEnv as ServerEnvironment] || DEFAULT_ENVIRONMENT;
};

/**
 * DoorDash integration base URLs by environment.
 *
 * There is no localhost entry: on a local dev server the environment detection
 * resolves to UAT, so DoorDash quotes/accepts go to the real deployed
 * integrations.uat surface (DoorDash stage sandbox) — never a local mock.
 */
export const DOORDASH_BASE_URLS: Record<string, string> = {
    stage: 'https://integrations.stage.wisesys.info',
    uat: 'https://integrations.uat.wisesys.info',
    demo: 'https://integrations.demo.wisesys.info',
    prod: 'https://integrations.wisesys.info'
};

export const getIntegrationsBaseUrl = (): string => {
    const env: string = getEnvironment();
    return DOORDASH_BASE_URLS[env] || DOORDASH_BASE_URLS[DEFAULT_ENVIRONMENT];
};
